import { MutationTree } from "vuex";
import {
	EventRecord,
	EventRecordFilter,
	EventRecordRestoreAt,
	EventRegion,
	MaskedAlarm,
	MaskReason,
	ShareWithPerson,
	SiteMonitorState
} from "./types";
import { AuditService } from "../../services/auditService";
import { formatDateMixin } from "@/mixins";
import { getDefaultState } from "./state";
import Vue from "vue";
import { SetEventRecordsPayload } from "./SetEventRecordsPayload";
import { formatRegionUrl } from "@/utils/string-utils";
import EventRecordTypes from "@/types/EventRecordTypes";
import { EventOutcomeResponse } from "@/types/EventOutcomeDTO";

const { dateStringWithZeroHourOffset } = formatDateMixin.methods;

export const mutations: MutationTree<SiteMonitorState> = {
	setAreaNotesVisible(state, isVisible: boolean) {
		state.AreaNotesVisible = isVisible;
	},
	resetEventRecords(state) {
		state.EventRecords = [];
		state.LatestEventRecordID = 0;
		state.AdhocLocationEventRecords = [];
		state.ActiveResponseIDs = {};

		state.EventRecordsAwaitingAck = {};
		state.EventRecordsAwaitingAckCount = 0;
		state.EventRecordsAwaitingRestore = {};
		state.EventRecordsAwaitingRestoreCount = 0;
		state.EventRecordsWithFiles = [];

		state.EventNoPermission = false;
		state.EventRecordsNoFilterRenderIndex = 0;

		state.EventRecordFilters.forEach((filter: EventRecordFilter) => {
			if (filter.showNew) {
				filter.newCount = 0;
			}
			filter.renderIndex = 0;
			filter.filterRecords = [];
		});
	},
	setEventRecordFilter(state, filterIndex: number) {
		state.EventRecordsFilterActiveIndex = filterIndex;
		const filter = state.EventRecordFilters[filterIndex];
		if (filter != null && filter.clearNewOnView) {
			filter.newCount = 0;
		}
	},
	addEventRecordFilter(state, filter: EventRecordFilter) {
		state.EventRecordFilters.push(filter);
	},
	setEventRecordFilterRenderIndex(state, { filterIndex, renderIndex }) {
		state.EventRecordFilters[filterIndex].renderIndex = renderIndex;
	},
	setEventRecordsNoFilterRenderIndex(state, renderIndex: number) {
		state.EventRecordsNoFilterRenderIndex = renderIndex;
	},
	setEventRecords(state, { eventRecords, orderTabsByNewest }: SetEventRecordsPayload) {
		if (eventRecords.length > 0) {
			const currentFilterIndex = state.EventRecordsFilterActiveIndex;
			const currentFilter = currentFilterIndex > -1 ? state.EventRecordFilters[currentFilterIndex] : null;
			const filters = state.EventRecordFilters;

			const newCheckingFilters = state.EventRecordFilters.filter(function(filter: any) {
				return filter.showNew && (!filter.clearNewOnView || currentFilterIndex !== filter.filterIndex);
			});

			const currentEventRecords = state.EventRecords;
			const activeResponseIDs = state.ActiveResponseIDs;

			const recordsAwaitingRestore = state.EventRecordsAwaitingRestore;
			const recordsAwaitingAck = state.EventRecordsAwaitingAck;

			const addedFilterCounts = {};
			filters.forEach((filter: any) => {
				addedFilterCounts[filter.filterIndex.toString()] = {
					count: 0,
					records: []
				};
			});

			for (let i = 0; i < eventRecords.length; i++) {
				const eventRecord = eventRecords[i];
				eventRecord.selected = false;
				eventRecord.expanded = false;
				eventRecord.camerasExpanded = false;

				if (eventRecord.eventRecordTypeID === 1) {
					if (eventRecord.awaitingAcknowledgement) {
						recordsAwaitingAck[eventRecord.eventRecordID.toString()] = eventRecord;
					}

					if (eventRecord.awaitingRestore != null) {
						recordsAwaitingRestore[eventRecord.eventRecordID.toString()] = eventRecord as EventRecordRestoreAt;
						Vue.set(eventRecord, "restoredAt", null);
					}

					if (eventRecord.objectID == null) {
						if (eventRecord.location !== "") {
							state.AdhocLocationEventRecords.push(eventRecord);
						}
					} else {
						if (activeResponseIDs[eventRecord.objectID.toString()] == null) {
							Vue.set(activeResponseIDs, eventRecord.objectID.toString(), eventRecord);
						}
					}

					if (eventRecord.details != null) {
						// Detect if the alarm has been identified as a runaway
						eventRecord.isRunaway = eventRecord.details.toUpperCase().startsWith("RUNAWAY");
					}
				}

				if (eventRecord.eventRecordTypeID === 21 && eventRecord.objectID !== null) {
					if (recordsAwaitingAck[eventRecord.objectID.toString()] != null) {
						recordsAwaitingAck[eventRecord.objectID.toString()].awaitingAcknowledgement = false;
						delete recordsAwaitingAck[eventRecord.objectID.toString()];
					}
				}

				if (eventRecord.eventRecordTypeID === 23 && eventRecord.objectID !== null) {
					if (!state.EventRecordsWithFiles.includes(eventRecord.objectID)) {
						state.EventRecordsWithFiles.push(eventRecord.objectID);
					}
				} else if (
					/* Accounts for uploading a file using the older Audit method called 'upload' */
					eventRecord.eventRecordTypeID === 23 &&
					eventRecord.objectID === null &&
					eventRecord.details.toLocaleLowerCase().startsWith("attached file:")
				) {
					state.EventRecordsWithFiles.push(eventRecord.eventRecordID);
				}

				const eventRecordTypeIdMediaMatrixSnapshot = 144;
				if (eventRecord.eventRecordTypeID === eventRecordTypeIdMediaMatrixSnapshot) {
					if (!state.EventRecordsWithFiles.includes(eventRecord.eventRecordID)) {
						state.EventRecordsWithFiles.push(eventRecord.eventRecordID);
					}
				}

				if (
					// Event record is restore, force restore or system force restore
					(eventRecord.eventRecordTypeID === EventRecordTypes.AlarmRestored
						|| eventRecord.eventRecordTypeID === EventRecordTypes.ForceRestore
						|| eventRecord.eventRecordTypeID === EventRecordTypes.SystemForceRestore)
					&& eventRecord.objectID !== null
				) {
					if (recordsAwaitingRestore[eventRecord.objectID.toString()] != null) {
						recordsAwaitingRestore[eventRecord.objectID.toString()].awaitingRestore = false;
						recordsAwaitingRestore[
							eventRecord.objectID.toString()
						].restoredAt = dateStringWithZeroHourOffset(eventRecord.created);
						// eventRecord.displayCreatedMoment;

						delete recordsAwaitingRestore[eventRecord.objectID.toString()];
					}
				}

				filters.forEach((filter: EventRecordFilter) => {
					if (filter.filterRecordIds.indexOf(eventRecord.eventRecordTypeID) > -1) {
						if (filter.showNew) {
							addedFilterCounts[filter.filterIndex.toString()].count++;
						}

						addedFilterCounts[filter.filterIndex.toString()].records.push(eventRecord);
					}
				});
			}

			filters.forEach((filter: EventRecordFilter) => {
				if (filter.showNew) {
					filter.newCount += addedFilterCounts[filter.filterIndex.toString()].count;
				}

				filter.filterRecords.push(...addedFilterCounts[filter.filterIndex.toString()].records);
			});

			if (orderTabsByNewest) {
				currentEventRecords.unshift(...eventRecords);
			} else {
				currentEventRecords.push(...eventRecords);
			}

			state.EventRecordsAwaitingAck = recordsAwaitingAck;
			state.EventRecordsAwaitingAckCount = Object.keys(recordsAwaitingAck).length;

			let eventRecordsAwaitingRestoreCount = 0;
			for (const eventRecordID in recordsAwaitingRestore) {
				if (recordsAwaitingRestore[eventRecordID].awaitingRestore) {
					eventRecordsAwaitingRestoreCount++;
				}
			}

			state.EventRecordsAwaitingRestoreCount = eventRecordsAwaitingRestoreCount;
			state.EventRecordsAwaitingRestore = recordsAwaitingRestore;

			state.ActiveResponseIDs = activeResponseIDs;
			state.EventRecords = currentEventRecords;
		}
	},
	setLatestEventRecordID(state, eventRecordID: number) {
		state.LatestEventRecordID = eventRecordID;
	},
	setEventDetails(state, eventDetails: any | null) {
		state.EventDetails = eventDetails;

		if (eventDetails == null) {
			state.IsController = false;
			state.auditService = null;
			state.GroupID = null;
			state.EventRecordsFilterActiveIndex = -1;
			state.activeMapItems = null;
			state.EventRecords = [];
			state.EventRecordsNoFilterRenderIndex = 0;
			state.SelectedEventRecord = null;
			state.EventRecordFilters.forEach((filter: any) => {
				if (filter.showNew) {
					filter.newCount = 0;
				}
				filter.renderIndex = 0;
				filter.filterRecords = [];
			});

			state.LatestEventRecordID = 0;
			state.LastActivity = null;
		} else {
			if (eventDetails.auditEndpoint != null) {
				state.auditService = new AuditService(eventDetails.auditEndpoint);
			}

			state.GroupID = eventDetails.groupID;
			state.LastActivity = new Date().valueOf();

			state.EventRecordsFilterActiveIndex = eventDetails.eventTypeID === 1 ? 0 : -1;
		}

		state.eventLocationLastSet = new Date().valueOf();
	},
	setEventRecordExpanded(state, { eventRecordID, expanded }: any) {
		state.EventRecords.find(e => e.eventRecordID == eventRecordID).expanded = expanded;
	},
	setEventRecordDetails(state, { eventRecordID, eventRecordDetails }: any) {
		state.EventRecords.find(e => e.eventRecordID == eventRecordID).eventRecordDetails = eventRecordDetails;
	},
	setGroupID(state, groupID: number) {
		state.GroupID = groupID;
	},
	setEventCloseShown(state, shown: boolean) {
		state.EventCloseOpen = shown;
	},
	setEventRaiseShown(state, shown: boolean) {
		state.EventRaiseOpen = shown;
	},
	setIncidentReportShown(state, shown: boolean) {
		state.IncidentReportOpen = shown;
	},
	setIncidentReportDownloaded(state, downloaded: boolean) {
		state.IncidentReportDownloaded = downloaded;
	},
	setEventDetailsShown(state, shown: boolean) {
		state.EventDetailsOpen = shown;
	},
	setPutEventOnTestShown(state, shown: boolean) {
		state.PutEventOnTestOpen = shown;
	},
	setEventShare(state, sharing: any[]) {
		state.EventShares = sharing;
	},
	setAdHocShareDetails(state, { eventUserID, shareLink, accessCode }: any) {
		state.AdhocShareDetails[eventUserID] = {
			shareLink,
			accessCode
		};
	},
	setShareWithPersons(state, data: ShareWithPerson[]) {
		state.ShareWithPersons = data;
	},
	setEventShareShown(state, shown: boolean) {
		state.EventSharingOpen = shown;
	},
	setUserID(state, UserID: number) {
		state.UserID = UserID;
	},
	setControllerUserID(state, id: number) {
		state.ControllerUserID = id;
	},
	setIsController(state, isController: boolean) {
		state.IsController = isController;
	},
	setEventLeaveShown(state, shown: boolean) {
		state.EventLeaveOpen = shown;
	},
	setExternalUsersAllowed(state, allow: boolean) {
		state.ExternalUsersAllowed = allow;
	},
	setForceRestoreEventRecord(state, record: EventRecord) {
		state.ForceRestoreEventRecord = record;
	},
	forceRestore(state) {
		const recordsAwaitingRestore = state.EventRecordsAwaitingRestore;

		state.ForceRestoreEventRecord.awaitingRestore = false;
		const eventRecord = state.ForceRestoreEventRecord;

		if (recordsAwaitingRestore[eventRecord.eventRecordID.toString()] != null) {
			recordsAwaitingRestore[eventRecord.eventRecordID.toString()].awaitingRestore = false;
			recordsAwaitingRestore[eventRecord.eventRecordID.toString()].restoredAt = eventRecord.displayCreatedMoment;

			delete recordsAwaitingRestore[eventRecord.eventRecordID.toString()];
		}

		let eventRecordsAwaitingRestoreCount = 0;
		for (const eventRecordID in recordsAwaitingRestore) {
			if (recordsAwaitingRestore[eventRecordID].awaitingRestore) {
				eventRecordsAwaitingRestoreCount++;
			}
		}

		state.EventRecordsAwaitingRestoreCount = eventRecordsAwaitingRestoreCount;
		state.EventRecordsAwaitingRestore = recordsAwaitingRestore;

		state.ForceRestoreEventRecord = null;
	},
	/* setUserShares(state, userShares: any) {
    state.userShares = userShares;
  }, */
	setEventSharesSet(state, set) {
		state.EventSharesSet = set;
	},
	setRequestingEventRecords(state, requesting: any) {
		state.requestingEventRecords = requesting;
	},
	setSelectedEventRecord(state, eventRecord: any) {
		if (state.SelectedEventRecord != null) {
			state.SelectedEventRecord.selected = false;
		}

		state.SelectedEventRecord = eventRecord;
		if (eventRecord != null) {
			state.SelectedEventRecord!.selected = true;
		}
	},
	setLocation(state, data: any) {
		state.EventDetails!.latLong = data.latLong;
		state.EventDetails!.minElevation = data.minElevation;
		state.EventDetails!.maxElevation = data.maxElevation;

		state.eventLocationLastSet = new Date().valueOf();
	},
	setActiveMapItems(state, data: any) {
		state.activeMapItems = data;
	},
	setEventNoPermission(state, noPermission: boolean) {
		state.EventNoPermission = noPermission;
	},
	setMapCircleCenter(state, mapCircleCenter: string) {
		state.mapCircleCenter = mapCircleCenter;
	},
	setEventRecordShares(state, userShares) {
		state.EventRecords.forEach(eventRecord => {
			if (eventRecord.eventUserID != null) {
				const userShare = userShares[eventRecord.eventUserID];

				if (userShare) {
					eventRecord.colorIndex = userShare.colorIndex;
					eventRecord.initials = userShare.initials;
					eventRecord.userName = userShare.name;
					eventRecord.userId = userShare.userID;
				} else {
					console.log(`Can't find user ${eventRecord.eventUserID}`);
					eventRecord.colorIndex = null;
					eventRecord.initials = null;
					eventRecord.userName = null;
					eventRecord.userId = null;
				}
			}
		});
	},
	setMaskReasons(state, data: MaskReason[]) {
		state.MaskReasons = data;
	},
	setGroupMaskReasons(state, data: MaskReason[]) {
		state.GroupMaskReasons = data;
	},
	setMaskedAlarms(state, data: MaskedAlarm[]) {
		state.MaskedAlarms = data;
	},
	setActivity(state, date) {
		state.LastActivity = date || new Date().valueOf();
	},
	setLastActivity(state, activityAt) {
		state.LastActivity = activityAt;
	},
	setActiveMapItemsRequired(state, required: boolean) {
		state.ActiveMapItemsRequired = required;
	},
	setEventRecordResponse(state, { eventRecordID, title, priority, responseID }: any) {
		const recordToUpdate = state.EventRecords.find(er => er.eventRecordID === eventRecordID);

		recordToUpdate.details = title;
		recordToUpdate.responsePriority = priority;
		recordToUpdate.objectID = responseID;
	},
	ackEventRecord(state, eventRecordID: number) {
		const recordToUpdate = state.EventRecords.find(er => er.eventRecordID === eventRecordID);
		recordToUpdate.awaitingAcknowledgement = false;
	},

	/**
	 * Sets the FlaggedForReview property for the current event (EventDetails).
	 * @param state The vuex state.
	 * @param flagged Whether or not the event has been flagged for review.
	 */
	setFlaggedForReview(state, flagged: boolean) {
		state.EventDetails.flaggedForReview = flagged;
	},

	setIsUploadingFile(state, uploading: boolean) {
		state.IsUploadingFile = uploading;
	},

	resetState(state) {
		Object.assign(state, getDefaultState());
	},

	setlastOperatorMessageVisible(state, isVisible: boolean) {
		state.LastOperatorMessageVisible = isVisible;
	},

	setRequestedRoute(state, requestedRoute: string) {
		state.RequestedRoute = requestedRoute;
	},
	setHideMapFlag(state, hideMapFlag: boolean) {
		state.hideMapFlag = hideMapFlag;
	},

	setEventRecordLocation(state, { eventRecordId, latLng, elevation }) {
		const eventRecord: EventRecord = state.EventRecords.find(x => x.eventRecordID == eventRecordId);
		if (!eventRecord) return;
		eventRecord.location = `${latLng.lat}, ${latLng.lng}`;
		eventRecord.latLong = `${latLng.lat}, ${latLng.lng}`;
		eventRecord.elevation = elevation;
	},

	/**
	 * Sets the event region, and updates the API base url.
	 * @param state the store.
	 * @param param1 the region id and url.
	 */
	setEventRegion(state, region: EventRegion) {
		state.eventRegion = {
			regionId: region.regionId,
			regionUrl: formatRegionUrl(region.regionUrl)
		};
	},

	setEventOutcomes(state, eventOutcomeResponse: EventOutcomeResponse) {
		state.eventOutcomes = eventOutcomeResponse;
	}
};
